import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { adminService } from '../../services/adminService';
import {
  FiSearch,
  FiFilter,
  FiEye,
  FiEdit,
  FiTrash2,
  FiUserPlus,
  FiChevronLeft,
  FiChevronRight,
  FiCalendar,
  FiRefreshCw
} from 'react-icons/fi';

const MerchantsListPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [merchants, setMerchants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterPackage, setFilterPackage] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    loadMerchants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterStatus, filterPackage, searchTerm, dateFrom, dateTo, sortBy, sortOrder]);

  const loadMerchants = async () => {
    try {
      setLoading(true);

      // Build query params
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder
      };

      // Multi-select status filter
      if (filterStatus.length > 0) {
        params.status = filterStatus.join(',');
      }

      // Multi-select package filter
      if (filterPackage.length > 0) {
        params.package = filterPackage.join(',');
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (dateFrom) {
        params.dateFrom = dateFrom;
      }

      if (dateTo) {
        params.dateTo = dateTo;
      }

      // Fetch merchants from API
      const response = await adminService.getAllMerchants(params);
      const apiMerchants = response.data.merchants.map(m => ({
        id: m._id,
        businessName: m.businessName,
        username: m.username,
        email: m.email,
        mobile: m.mobile,
        location: m.location || 'N/A',
        address: m.address || 'N/A',
        status: m.subscription.status,
        subscription: m.subscription.package || 'trial',
        registrationDate: new Date(m.createdAt).toLocaleDateString('en-GB'),
        totalDebtors: m.stats?.totalDebtors || 0,
        totalOutstanding: m.stats?.totalOutstanding || 0
      }));

      setMerchants(apiMerchants);
      setTotalCount(response.data.pagination.total);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      console.error('Error loading merchants:', error);
      setMerchants([]);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus([]);
    setFilterPackage([]);
    setDateFrom('');
    setDateTo('');
    setSortBy('createdAt');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filterStatus.length > 0) count++;
    if (filterPackage.length > 0) count++;
    if (dateFrom || dateTo) count++;
    return count;
  };

  const toggleStatusFilter = (status) => {
    setFilterStatus(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
    setCurrentPage(1);
  };

  const togglePackageFilter = (pkg) => {
    setFilterPackage(prev =>
      prev.includes(pkg)
        ? prev.filter(p => p !== pkg)
        : [...prev, pkg]
    );
    setCurrentPage(1);
  };

  const paginatedMerchants = merchants;

  const handleExport = async (format) => {
    try {
      // Build filters from current state
      const filters = {};

      if (filterStatus.length > 0) {
        filters.status = filterStatus.join(',');
      }

      if (filterPackage.length > 0) {
        filters.package = filterPackage.join(',');
      }

      if (searchTerm) {
        filters.search = searchTerm;
      }

      if (dateFrom) {
        filters.dateFrom = dateFrom;
      }

      if (dateTo) {
        filters.dateTo = dateTo;
      }

      filters.sortBy = sortBy;
      filters.sortOrder = sortOrder;

      // Get the export URL from service
      let result;
      if (format === 'excel') {
        result = await adminService.exportMerchantsExcel(filters);
      } else if (format === 'pdf') {
        result = await adminService.exportMerchantsPDF(filters);
      }

      // Get auth token from localStorage
      const token = localStorage.getItem('token');

      // Create full URL with API base and token
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
      const fullUrl = `${apiUrl}${result.url}`;

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = fullUrl;
      link.setAttribute('download', `merchants_${Date.now()}.${format === 'excel' ? 'xlsx' : 'pdf'}`);

      // Add authorization header by opening in new window with proper headers
      // For downloads, we need to use fetch with blob
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        link.href = blobUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        console.error('Export failed:', response.statusText);
        alert('Export failed. Please try again.');
      }
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Export failed. Please try again.');
    }
  };

  const handleView = (merchantId) => {
    navigate(`/admin/merchants/${merchantId}`);
  };

  const handleEdit = (merchantId) => {
    navigate(`/admin/merchants/${merchantId}/edit`);
  };

  const handleDelete = async (merchantId) => {
    if (window.confirm('Are you sure you want to delete this merchant? This action cannot be undone.')) {
      try {
        await adminService.deleteMerchant(merchantId);
        alert('Merchant deleted successfully');
        // Reload merchants list
        loadMerchants();
      } catch (error) {
        console.error('Error deleting merchant:', error);
        alert('Failed to delete merchant. Please try again.');
      }
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      expired: 'bg-red-100 text-red-800',
      inactive: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badges[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getSubscriptionBadge = (subscription) => {
    const badges = {
      trial: 'bg-blue-100 text-blue-800',
      base: 'bg-green-100 text-green-800',
      basic_plus: 'bg-indigo-100 text-indigo-800'
    };

    const labels = {
      trial: 'Trial',
      base: 'Base',
      basic_plus: 'Pro'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badges[subscription]}`}>
        {labels[subscription]}
      </span>
    );
  };

  if (loading) {
    return (
      <AdminLayout title="Merchants">
        <LoadingSpinner fullScreen />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Merchants">
      <div className="space-y-6">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow p-6">
          {/* Main Filter Row */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
            {/* Search */}
            <div className="md:col-span-5">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, username, email, or mobile"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Sort By */}
            <div className="md:col-span-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="createdAt">Registration Date</option>
                <option value="businessName">Business Name</option>
                <option value="totalDebtors">Total Debtors</option>
                <option value="totalOutstanding">Outstanding Amount</option>
              </select>
            </div>

            {/* Sort Order */}
            <div className="md:col-span-1">
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="desc">↓</option>
                <option value="asc">↑</option>
              </select>
            </div>

            {/* Export Buttons */}
            <div className="md:col-span-2 flex space-x-2">
              <button
                onClick={() => handleExport('excel')}
                className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                title="Export to Excel"
              >
                Excel
              </button>
              <button
                onClick={() => handleExport('pdf')}
                className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                title="Export to PDF"
              >
                PDF
              </button>
            </div>

            {/* Add New Button */}
            <div className="md:col-span-2">
              <button
                onClick={() => navigate('/admin/merchants/new')}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center space-x-2"
              >
                <FiUserPlus className="w-4 h-4" />
                <span className="text-sm font-medium">Add New</span>
              </button>
            </div>
          </div>

          {/* Advanced Filters Toggle */}
          <div className="flex items-center justify-between border-t border-gray-200 pt-4">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="flex items-center space-x-2 text-indigo-600 hover:text-indigo-700 font-medium"
            >
              <FiFilter className="w-4 h-4" />
              <span>Advanced Filters</span>
              {getActiveFiltersCount() > 0 && (
                <span className="px-2 py-1 bg-indigo-600 text-white text-xs rounded-full">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>
            {getActiveFiltersCount() > 0 && (
              <button
                onClick={clearFilters}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-700 text-sm"
              >
                <FiRefreshCw className="w-4 h-4" />
                <span>Clear All Filters</span>
              </button>
            )}
          </div>

          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Status Filter - Multi-select */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <div className="space-y-2">
                  {['active', 'pending', 'expired', 'inactive'].map((status) => (
                    <label key={status} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filterStatus.includes(status)}
                        onChange={() => toggleStatusFilter(status)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700 capitalize">{status}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Package Filter - Multi-select */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Package</label>
                <div className="space-y-2">
                  {[
                    { value: 'trial', label: 'Trial' },
                    { value: 'base', label: 'Base' },
                    { value: 'basic_plus', label: 'Pro' }
                  ].map((pkg) => (
                    <label key={pkg.value} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filterPackage.includes(pkg.value)}
                        onChange={() => togglePackageFilter(pkg.value)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">{pkg.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Date Range Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FiCalendar className="inline w-4 h-4 mr-1" />
                  Registration Date Range
                </label>
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">From</label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => {
                        setDateFrom(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">To</label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => {
                        setDateTo(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Merchants Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Merchant ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Business Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Subscription
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Debtors
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Outstanding
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedMerchants.map((merchant) => (
                  <tr key={merchant.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {merchant.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{merchant.businessName}</div>
                        <div className="text-sm text-gray-500">@{merchant.username}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{merchant.mobile}</div>
                      <div className="text-sm text-gray-500">{merchant.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{merchant.location}</div>
                      <div className="text-xs text-gray-500">{merchant.address}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(merchant.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getSubscriptionBadge(merchant.subscription)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {merchant.totalDebtors}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      BHD {merchant.totalOutstanding.toFixed(3)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleView(merchant.id)}
                          className="text-indigo-600 hover:text-indigo-900 p-1"
                          title="View"
                        >
                          <FiEye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(merchant.id)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="Edit"
                        >
                          <FiEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(merchant.id)}
                          className="text-red-600 hover:text-red-900 p-1"
                          title="Delete"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Showing page {currentPage} of {totalPages} ({totalCount} total merchants)
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 rounded-lg transition-colors ${
                    currentPage === page
                      ? 'bg-indigo-600 text-white'
                      : 'border border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              ))}
              {totalPages > 5 && <span className="px-2">...</span>}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default MerchantsListPage;
